import React, { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Badge } from 'react-bootstrap'
import { FaTrash, FaPlus } from 'react-icons/fa'
import { useDispatch, useSelector } from 'react-redux'
import { motion, AnimatePresence } from 'framer-motion'
import CvNavigation from '../../../components/CvNavigation'
import CvPageLayout from '../../../components/CvPageLayout'
import SearchableDropdown from '../../../components/SearchableDropdown'
import DeleteModal from '../../../components/DeleteModal'
import { fetchLanguage, deleteLanguage, createLanguage, updateLanguage } from '../../../actions/languageActions'

const LEVELS = [
  { label: 'A1', value: 0, desc: 'Początkujący' },
  { label: 'A2', value: 20, desc: 'Podstawowy' },
  { label: 'B1', value: 40, desc: 'Średnio zaawansowany' },
  { label: 'B2', value: 60, desc: 'Wyższy średnio zaawansowany' },
  { label: 'C1', value: 80, desc: 'Zaawansowany' },
  { label: 'C2', value: 100, desc: 'Biegły' }
]

function getLevelLabel(val) {
  let closest = LEVELS[0];
  for (let lvl of LEVELS) {
    if (Math.abs(val - lvl.value) < Math.abs(val - closest.value)) {
      closest = lvl;
    }
  }
  return closest.label;
}

function CvGeneratorLanguages() {
  const dispatch = useDispatch();

  const { languages, loading, error } = useSelector(state => state.languageList || {});

  const [nativeLanguage, setNativeLanguage] = useState('');
  const [foreignLanguages, setForeignLanguages] = useState([{ language: '', level: 0 }]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [languageToDelete, setLanguageToDelete] = useState(null);

  const allLanguages = [
    "Polski", "Angielski", "Niemiecki", "Francuski", "Hiszpański",
    "Włoski", "Portugalski", "Holenderski", "Szwedzki", "Norweski",
    "Duński", "Fiński", "Islandzki", "Irlandzki", "Czeski", "Słowacki",
    "Węgierski", "Rumuński", "Bułgarski", "Chorwacki", "Serbski",
    "Bośniacki", "Słoweński", "Litewski", "Łotewski", "Estoński",
    "Ukraiński", "Białoruski", "Rosyjski", "Grecki", "Turecki",
    "Chiński", "Japoński", "Koreański", "Hindi", "Arabski", "Hebrajski",
    "Perski", "Esperanto", "Łacina", "Tajski", "Wietnamski", "Indonezyjski",
    "Malajski", "Filipiński", "Khmerski", "Lao", "Birmański", "Mongolski",
    "Tamilski", "Telugu", "Marathi", "Nepalski", "Sinhala", "Bengalski",
    "Pendżabski", "Urdu", "Paszto", "Kurdyjski", "Somalijski", "Suahili",
    "Amharski", "Hausa", "Joruba", "Zulu", "Igbo", "Haitański Kreolski",
    "Guarani", "Keczua", "Aymara", "Nahua", "Sanskryt", "Gruziński",
    "Armeński", "Azerbejdżański", "Kazachski", "Uzbecki", "Turkmeński",
    "Tadżycki", "Kirgiski", "Ujgur", "Tatar", "Baszkirski", "Czeczeński",
    "Mołdawski", "Macedoński", "Albanski", "Baskijski", "Kataloński",
    "Galicyjski", "Bretoński", "Walijski", "Szkocki Gaelicki", "Manx",
    "Kornijski", "Fryzyjski", "Luksemburski", "Sardyński", "Maltański",
    "Buriacki", "Tuwiński", "Jakucki", "Czukocki", "Ewenkijski"
  ];

  useEffect(() => {
    dispatch(fetchLanguage());
  }, [dispatch]);

  useEffect(() => {
    if (languages && Array.isArray(languages)) {
      const mappedLanguages = languages.map(l => {
        const levelObj =
          LEVELS.find(lvl =>
            typeof l.level === 'string'
              ? lvl.label === l.level
              : lvl.value === l.level
          ) || LEVELS[0]; 

        return {
          ...l,
          level: levelObj.value, 
          desc: levelObj.desc,   
          type:
            l.type === 'język ojczysty'
              ? 'native'
              : l.type === 'język obcy'
              ? 'foreign'
              : l.type
        };
      });

      const native = mappedLanguages.find(l => l.type === 'native');
      const foreign = mappedLanguages.filter(l => l.type === 'foreign');

      // Ustaw język ojczysty tylko jeśli nie jest już ustawiony
      if (!nativeLanguage && native) {
        setNativeLanguage(native.name);
      }
      
      // Aktualizuj języki obce, ale zachowaj już wybrane
      const updatedForeign = foreign.length > 0
        ? foreign.map(f => ({
            id: f.id,
            language: f.name,
            level: f.level || 0
          }))
        : [{ language: '', level: 0 }];
      
      // Jeśli mamy wybrane języki obce, nie resetuj ich
      if (foreignLanguages.length > 0 && foreignLanguages[0].language) {
        // Merge: utrzymaj wybrane języki, dodaj nowe z serwera
        const merged = foreignLanguages.map(fl => {
          const serverVersion = updatedForeign.find(uf => uf.language === fl.language);
          return serverVersion ? { ...serverVersion, level: fl.level !== 0 ? fl.level : serverVersion.level } : fl;
        });
        setForeignLanguages(merged);
      } else {
        setForeignLanguages(updatedForeign);
      }
    }
  }, [languages]);

  const handleNativeLanguageSelect = (language) => {
    setNativeLanguage(language);
  };

  const handleForeignLanguageSelect = (language, idx) => {
    const updated = [...foreignLanguages];
    updated[idx].language = language;
    updated[idx].level = 0; 
    setForeignLanguages(updated);
    dispatch(createLanguage(language, "A1", 'język obcy'));
  };

  const handleAddForeignLanguage = () => {
    handleSaveAllLanguages();
    setForeignLanguages([...foreignLanguages, { language: '', level: 0 }]);
  };

  const handleLevelChange = (idx, value) => {
    const updated = [...foreignLanguages];
    updated[idx].level = value;
    setForeignLanguages(updated);
  };

  const handleDeleteClick = (idx) => {
    handleSaveAllLanguages();
    const langId = foreignLanguages[idx].id;
    if (langId) {
      setLanguageToDelete(langId);
      setShowDeleteModal(true);
    } else {
      // Jeśli język nie ma ID (nowy, nie zapisany), usuń go ze stanu
      const updated = foreignLanguages.filter((_, i) => i !== idx);
      setForeignLanguages(updated.length > 0 ? updated : [{ language: '', level: 0 }]);
    }
  };

  const confirmDelete = () => {
    if (languageToDelete) {
      dispatch(deleteLanguage(languageToDelete));
      // Usuń z local state
      const updated = foreignLanguages.filter(l => l.id !== languageToDelete);
      setForeignLanguages(updated.length > 0 ? updated : [{ language: '', level: 0 }]);
    }
    setShowDeleteModal(false);
    setLanguageToDelete(null);
  };

  const getAvailableLanguages = (idx) => {
    const selected = [nativeLanguage, ...foreignLanguages.map((l, i) => i !== idx ? l.language : null).filter(Boolean)];
    return allLanguages.filter(lang => !selected.includes(lang));
  };

  const handleSaveAllLanguages = () => {
    const native = languages?.find(l => l.type === 'język ojczysty');
    const nativeLength = nativeLanguage?.length || 0;
    
    if (nativeLanguage && nativeLanguage.length > 0) {
      if (native) {      
        dispatch(updateLanguage(native.id, nativeLanguage, 'C2', 'język ojczysty', nativeLength));
      } else {
        dispatch(createLanguage(nativeLanguage, 'C2', 'język ojczysty', nativeLength));
      }
    } else {
      if (native) {
        dispatch(updateLanguage(native.id, "", 'C2', 'język ojczysty', nativeLength));
      }
    }  

    foreignLanguages.forEach(lang => {
      if (lang.language) {
        const existing = languages?.find(
          l => l.name === lang.language && l.type === 'język obcy'
        );

        const levelLabel = getLevelLabel(lang.level);

        if (existing) {
          dispatch(updateLanguage(existing.id, lang.language, levelLabel, 'język obcy'));
        } else {
          dispatch(createLanguage(lang.language, levelLabel, 'język obcy'));
        }
      }
    });
  };

  if (loading) {
    return <div className="text-center mt-5">Ładowanie języków...</div>;
  }

  if (error) {
    return <div className="text-danger text-center mt-5">Błąd: {error}</div>;
  }

  return (
    <CvPageLayout currentStep="Języki">
      <Row className="justify-content-md-center mt-2">
        <Col>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="fw-bold">
              Ile znasz języków?
            </h3>
            <p className="text-muted">Dodaj swój język ojczysty i wszystkie języki obce, które znasz.</p>
          </motion.div>
        </Col>
      </Row>

      {/* JĘZYK OJCZYSTY */}
      <Row className="justify-content-xs-center mt-4">
        <Col xs={12} md={8} lg={4}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <SearchableDropdown
              options={allLanguages.filter(lang => !foreignLanguages.map(l => l.language).includes(lang))}
              onSelect={handleNativeLanguageSelect}
              value={nativeLanguage}
              label="Język ojczysty"
            />
          </motion.div>
        </Col>
      </Row>

      {/* JĘZYKI OBCE */}
      <AnimatePresence>
        {foreignLanguages.map((langObj, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, delay: idx * 0.1 }}
          >
            <Row className="justify-content-xs-center mt-4">
              <Col xs={12} lg={langObj.language ? 12 : 4}>
                {!langObj.language ? (
                  <SearchableDropdown
                    options={getAvailableLanguages(idx)}
                    onSelect={(language) => handleForeignLanguageSelect(language, idx)}
                    value={langObj.language}
                    label="Język obcy"
                  />
                ) : (
                  <Card
                    className="shadow-lg border-0"
                    style={{
                      borderRadius: '20px',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <Card.Body className="p-3 p-md-4">
                      <Row className="align-items-start g-3">
                        {/* NUMER */}
                        <Col xs="auto">
                          <div
                            className="d-flex align-items-center justify-content-center text-white fw-bold rounded-circle"
                            style={{
                              width: '45px',
                              height: '45px',
                              fontSize: '1.3rem',
                              background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
                              flexShrink: 0
                            }}
                          >
                            {idx + 1}
                          </div>
                        </Col>

                        {/* TREŚĆ */}
                        <Col>
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                              <h5 className="fw-bold mb-1">{langObj.language}</h5>
                              <div className="d-flex gap-2 flex-wrap">
                                <Badge 
                                  bg="light" 
                                  text="dark"
                                  style={{ fontSize: '0.85rem', fontWeight: '500' }}
                                >
                                  Poziom: <span className="fw-bold text-primary">{getLevelLabel(langObj.level)}</span>
                                </Badge>
                              </div>
                            </div>

                            {/* PRZYCISK USUŃ */}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteClick(idx)}
                              style={{
                                borderRadius: '10px',
                                padding: '0.4rem 0.6rem'
                              }}
                            >
                              <FaTrash />
                            </Button>
                          </div>

                          {/* SUWAK POZIOMU */}
                          <div>
                            <div
                              className="mb-2 fw-semibold text-primary"
                              style={{ fontSize: '0.95rem' }}
                            >
                              {LEVELS.find(lvl => lvl.value === langObj.level)?.desc}
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              step={20}
                              value={langObj.level}
                              onChange={e => handleLevelChange(idx, Number(e.target.value))}
                              style={{
                                width: '100%',
                                accentColor: 'var(--bs-primary)',
                                cursor: 'pointer',
                                height: '8px'
                              }}
                            />
                            <div className="d-flex justify-content-between mt-1">
                              {LEVELS.map(lvl => (
                                <small 
                                  key={lvl.value} 
                                  className="text-muted"
                                  style={{ fontSize: '0.7rem' }}
                                >
                                  {lvl.label}
                                </small>
                              ))}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                {/* DODAJ KOLEJNY JĘZYK */}
                {idx === foreignLanguages.length - 1 && (
                  <motion.div
                    className="mt-3"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div
                      className="text-primary fw-medium"
                      style={{
                        cursor: langObj.language ? 'pointer' : 'default',
                        fontSize: '0.95rem',
                        opacity: langObj.language ? 1 : 0.5,
                        transition: 'all 0.2s'
                      }}
                      onClick={langObj.language ? handleAddForeignLanguage : undefined}
                      onMouseEnter={(e) => {
                        e.target.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.textDecoration = 'none';
                      }}
                    >
                      <FaPlus className="me-2" size={14} />
                      Dodaj kolejny język obcy
                    </div>
                  </motion.div>
                )}
              </Col>
            </Row>
          </motion.div>
        ))}
      </AnimatePresence>

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
      />

      <CvNavigation
        backlink="/cv-career/cv-generator/section/education"
        nextlink="/cv-career/cv-generator/section/bonus-info"
        onNext={handleSaveAllLanguages}
      />
    </CvPageLayout>
  )
}

export default CvGeneratorLanguages